import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Typography,
  FormGroup,
  FormControlLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Project, ProjectCreate, ProjectUpdate } from '../types/project';
import { userApi } from '../api/userApi';
import { projectUserApi } from '../api/projectUserApi';
import { User, UserRole } from '../types/models';
import { isSystemAdminUser } from '../utils/permissions';

interface ProjectFormData {
  code: string;
  name: string;
  description?: string;
  user_roles?: { user_id: number; role: string }[];
}

interface ProjectFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ProjectCreate | ProjectUpdate) => Promise<void>;
  project?: Project;
  onSuccess?: (message: string) => void;
}

const roleOptions = [
  { value: UserRole.ADMIN, label: 'Admin' },
  { value: UserRole.APPROVER, label: 'Approver' },
  { value: UserRole.CM_PIC, label: 'CM PIC' },
  { value: UserRole.ISSUER, label: 'Issuer' },
];

const DEFAULT_ROLE = UserRole.ISSUER;

type SelectedUserRoleState = { user_id: number; roles: string[] };

const aggregateUserRoles = (userRoles: { user_id: number; role: string }[]): SelectedUserRoleState[] => {
  const map = new Map<number, Set<string>>();
  userRoles.forEach(({ user_id, role }) => {
    if (!map.has(user_id)) {
      map.set(user_id, new Set());
    }
    map.get(user_id)!.add(role);
  });

  return Array.from(map.entries()).map(([user_id, roles]) => ({
    user_id,
    roles: Array.from(roles),
  }));
};

const flattenUserRoles = (userRoles: SelectedUserRoleState[]): { user_id: number; role: string }[] =>
  userRoles.flatMap((userRole) =>
    userRole.roles.map((role) => ({
      user_id: userRole.user_id,
      role,
    }))
  );

const ensureAtLeastOneRole = (roles: string[]): string[] => {
  if (!roles.length) {
    return [DEFAULT_ROLE];
  }
  return Array.from(new Set(roles));
};

export const ProjectFormDialog: React.FC<ProjectFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  project,
  onSuccess,
}) => {
  const [apiError, setApiError] = React.useState<string | null>(null);
  const [users, setUsers] = React.useState<User[]>([]);
  const [selectedUserRoles, setSelectedUserRoles] = React.useState<SelectedUserRoleState[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = React.useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProjectFormData>({
    defaultValues: {
      code: project?.code || '',
      name: project?.name || '',
      description: project?.description || '',
    },
  });

  // Fetch users when dialog opens
  React.useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const usersData = await userApi.getUsers();
        const activeUsers = usersData.filter((user) => user.is_active);
        setUsers(activeUsers);
      } catch (error) {
        console.error('Failed to fetch users:', error);
      } finally {
        setIsLoadingUsers(false);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open]);

  // Fetch project's associated users
  React.useEffect(() => {
    const fetchProjectUsers = async () => {
      if (project?.id) {
        try {
          const projectUserRoles = await projectUserApi.getProjectUserRoles(project.id);
          
          // Filter out system admin users from the selected users
          const filteredUserRoles = projectUserRoles.filter(userRole => {
            const user = users.find(u => u.id === userRole.user_id);
            return user && !isSystemAdminUser(user);
          });
          
          const aggregated = aggregateUserRoles(filteredUserRoles);
          setSelectedUserRoles(aggregated);
          setValue('user_roles', flattenUserRoles(aggregated));
        } catch (error) {
          console.error('Failed to fetch project user roles:', error);
          setSelectedUserRoles([]);
          setValue('user_roles', []);
        }
      }
    };

    if (open && project) {
      fetchProjectUsers();
    } else {
      setSelectedUserRoles([]);
      setValue('user_roles', []);
    }
  }, [open, project, users, setValue]);

  React.useEffect(() => {
    if (open) {
      setApiError(null);
      // Only reset when dialog opens or project changes, not when user selection changes
      if (!project) {
        reset({
          code: '',
          name: '',
          description: '',
          user_roles: [],
        });
      } else {
        reset({
          code: project.code || '',
          name: project.name || '',
          description: project.description || '',
          user_roles: flattenUserRoles(selectedUserRoles),
        });
      }
    }
  }, [open, project, reset]);

  // Separate effect to update just the user_roles field when selectedUserRoles changes
  React.useEffect(() => {
    if (open) {
      setValue('user_roles', flattenUserRoles(selectedUserRoles));
    }
  }, [selectedUserRoles, setValue, open]);

  const handleUserSelectionChange = (selectedUserIds: number[], onChange: (value: any) => void) => {
    const newSelectedUserRoles: SelectedUserRoleState[] = selectedUserIds.map(userId => {
      const existing = selectedUserRoles.find(ur => ur.user_id === userId);
      if (existing) {
        return existing;
      }
      return { user_id: userId, roles: [DEFAULT_ROLE] };
    });

    setSelectedUserRoles(newSelectedUserRoles);
    onChange(flattenUserRoles(newSelectedUserRoles));
  };

  const toggleUserRole = (userId: number, role: string, checked: boolean) => {
    setSelectedUserRoles(prev =>
      prev.map(userRole => {
        if (userRole.user_id !== userId) return userRole;
        const updatedRoles = checked
          ? ensureAtLeastOneRole([...userRole.roles, role])
          : ensureAtLeastOneRole(userRole.roles.filter(r => r !== role));
        return { ...userRole, roles: updatedRoles };
      })
    );
  };

  const handleFormSubmit = async (data: ProjectFormData) => {
    try {
      setApiError(null);
      await onSubmit(data);
      const message = project ? 'Event updated successfully' : 'Event created successfully';
      onSuccess?.(message);
      onClose();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'An error occurred while saving the project';
      setApiError(errorMessage);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{project ? 'Edit Event' : 'Create Event'}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {apiError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="code"
              control={control}
              rules={{
                required: 'Code is required',
                pattern: {
                  value: /^[A-Za-z0-9-_]+$/,
                  message: 'Code can only contain letters, numbers, hyphens, and underscores',
                },
              }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Code"
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  multiline
                  rows={2}
                  error={!!errors.description}
                  helperText={errors.description?.message}
                  fullWidth
                />
              )}
            />

            {/* User Selection */}
            <FormControl fullWidth>
              <InputLabel id="user-select-label">Authorized Users</InputLabel>
              <Controller
                name="user_roles"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="user-select-label"
                    label="Authorized Users"
                    multiple
                    value={selectedUserRoles.map(ur => ur.user_id)}
                    onChange={(e) => {
                      const selectedUserIds = e.target.value as number[];
                      handleUserSelectionChange(selectedUserIds, field.onChange);
                    }}
                    renderValue={(selected) => (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {(selected as number[]).map((userId) => {
                          const user = users.find(u => u.id === userId);
                          // Skip rendering system admin users in chips
                          if (user && isSystemAdminUser(user)) {
                            return null;
                          }
                          return (
                            <Chip key={userId} label={user?.full_name || `User ${userId}`} />
                          );
                        }).filter(Boolean)}
                      </Box>
                    )}
                    disabled={isLoadingUsers}
                  >
                    {users
                      .filter(user => !isSystemAdminUser(user))
                      .map((user) => (
                        <MenuItem key={user.id} value={user.id}>
                          <Checkbox checked={selectedUserRoles.some(ur => ur.user_id === user.id)} />
                          <ListItemText primary={user.full_name} secondary={user.email} />
                        </MenuItem>
                      ))}
                  </Select>
                )}
              />
            </FormControl>

            {/* Role Assignment */}
            {selectedUserRoles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>Assign Roles</Typography>
                {selectedUserRoles.map((userRole) => {
                  const user = users.find(u => u.id === userRole.user_id);
                  return (
                    <Box key={userRole.user_id} sx={{ mb: 1, borderTop: '1px solid #e8e8e8' }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography sx={{ fontWeight: 600 }}>
                          {user?.full_name || `User ${userRole.user_id}`}
                        </Typography>
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            const newSelectedUserRoles = selectedUserRoles.filter(
                              (ur) => ur.user_id !== userRole.user_id
                            );
                            setSelectedUserRoles(newSelectedUserRoles);
                            setValue('user_roles', flattenUserRoles(newSelectedUserRoles));
                          }}
                        >
                          Remove
                        </Button>
                      </Box>
                      <FormGroup row>
                        {roleOptions.map(({ value, label }) => (
                          <FormControlLabel
                            key={`${userRole.user_id}-${value}`}
                            control={
                              <Checkbox
                                checked={userRole.roles.includes(value)}
                                onChange={(e) => {
                                  toggleUserRole(userRole.user_id, value, e.target.checked);
                                  setValue('user_roles', flattenUserRoles(selectedUserRoles));
                                }}
                              />
                            }
                            label={label}
                          />
                        ))}
                      </FormGroup>
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting || isLoadingUsers}
          >
            {project ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
